import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/store';
import type {
  DriverChatConnection,
  DriverChatSeed,
} from '@component/DriverChat/types';

// Drafts live in memory only: chat text is never written to browser storage.
type DriverChatState = {
  isPanelOpen: boolean;
  panelConversationId: number | null;
  /** Always carries a conversationId: the entry button opens the conversation first. */
  drawerSeed: DriverChatSeed | null;
  drafts: Record<number, string>;
  /** Driver phone/photo captured when a chat was started; the API does not return them. */
  drivers: Record<number, { phone?: string; image?: string }>;
  /** Segments on screen whose driver has accepted: lets the launcher appear without a live event. */
  trackedSegments: Record<number, DriverChatSeed>;
  /** Once a driver has accepted a ride, the launcher stays on every screen. */
  isLauncherLatched: boolean;
  connection: DriverChatConnection;
};

const initialState: DriverChatState = {
  isPanelOpen: false,
  panelConversationId: null,
  drawerSeed: null,
  drafts: {},
  drivers: {},
  trackedSegments: {},
  isLauncherLatched: false,
  connection: 'connecting',
};

const driverChatSlice = createSlice({
  name: 'driverChat',
  initialState,
  reducers: {
    openDriverChatDrawer(state, action: PayloadAction<DriverChatSeed>) {
      state.drawerSeed = action.payload;
      state.isPanelOpen = false;
    },
    closeDriverChatDrawer(state) {
      state.drawerSeed = null;
    },
    toggleDriverChatPanel(state) {
      state.isPanelOpen = !state.isPanelOpen;
      if (state.isPanelOpen) state.drawerSeed = null;
    },
    closeDriverChatPanel(state) {
      state.isPanelOpen = false;
    },
    openPanelConversation(state, action: PayloadAction<number | null>) {
      state.isPanelOpen = true;
      state.panelConversationId = action.payload;
    },
    setDriverChatDraft(
      state,
      action: PayloadAction<{ conversationId: number; text: string }>
    ) {
      const { conversationId, text } = action.payload;
      if (text) state.drafts[conversationId] = text;
      else delete state.drafts[conversationId];
    },
    rememberChatDriver(
      state,
      action: PayloadAction<{ segmentId: number; phone?: string; image?: string }>
    ) {
      const { segmentId, phone, image } = action.payload;
      // A newer value replaces the old one; an absent one never erases what we know.
      state.drivers[segmentId] = {
        ...state.drivers[segmentId],
        ...(phone ? { phone } : {}),
        ...(image ? { image } : {}),
      };
    },
    trackChatSegment(state, action: PayloadAction<DriverChatSeed>) {
      state.trackedSegments[action.payload.segmentId] = action.payload;
    },
    untrackChatSegment(state, action: PayloadAction<number>) {
      delete state.trackedSegments[action.payload];
    },
    latchDriverChatLauncher(state) {
      state.isLauncherLatched = true;
    },
    setDriverChatConnection(
      state,
      action: PayloadAction<DriverChatConnection>
    ) {
      state.connection = action.payload;
    },
    resetDriverChat() {
      return initialState;
    },
  },
});

export const {
  openDriverChatDrawer,
  closeDriverChatDrawer,
  toggleDriverChatPanel,
  closeDriverChatPanel,
  openPanelConversation,
  setDriverChatDraft,
  rememberChatDriver,
  trackChatSegment,
  untrackChatSegment,
  latchDriverChatLauncher,
  setDriverChatConnection,
  resetDriverChat,
} = driverChatSlice.actions;

export const selectDriverChat = (state: RootState) => state.driverChat;

/** Conversation ids whose thread is currently on screen (drawer or panel). */
export const selectVisibleConversationIds = (state: RootState) => {
  const { drawerSeed, isPanelOpen, panelConversationId } = state.driverChat;
  const ids: number[] = [];
  if (drawerSeed?.conversationId != null) ids.push(drawerSeed.conversationId);
  if (isPanelOpen && panelConversationId != null) ids.push(panelConversationId);
  return ids;
};

export const driverChatReducer = driverChatSlice.reducer;
